"use client";
import React, { useCallback, useState } from "react";
import { Box, Button as MUIButton } from "@mui/material";
import Image from "next/image";
import { Label, FileInput } from "@int-traderjoes/ui-components-library";
import downloadButton from "@/assets/images/ideation-phase-images/DownloadButton.svg";
import { deleteIcon } from "@/assets/images/ideation-phase-images";
import DownloadButton from "@/components/shared/DownloadButton";
import FileNameTruncatedText from "@/components/Common/FileNameTruncatedText";
import ConfirmationModal from "@/components/ConfirmationModal";
import {
  setConfirmationMessage,
  setConfirmationTittle,
  setHighlightedConfirmationMessage,
} from "@/components/DataTable/Datatable.variables";
import { useLanguageContext } from "@/context/LanguageContext";
import { fileActionButtonStyles } from "@/styles/sx";
import {
  fileDisplayContainerSx,
  fileNameTruncationSx,
  fileActionButtonsContainerSx,
  fileActionButtonBaseSx,
  fileUploadRootSx,
} from "@/styles/sx/fileUpload";
import "./FileUploadDisplay.scss";

// Compliance keeps a fixed action-area size so icon/text download buttons align consistently
const actionButtonsContainerSx = {
  ...fileActionButtonsContainerSx,
  minWidth: "50px",
  height: "23px",
};

interface FileUploadDisplayProps {
  // File upload props
  hasUploadedFile: boolean;
  fileName?: string;
  isUploading: boolean;
  
  // File input props (when no file uploaded)
  onFileChange: (file: File | null) => void;
  placeholder?: string;
  fileInputValue?: File | null;
  fileInputKey?: string;
  allowedTypes?: string[]; // Array of file extensions without dots (e.g., ['pdf', 'doc', 'docx'])
  toastMessage?: string; // Custom error message for invalid file type
  
  // File actions (when file uploaded)
  onDownload: () => void;
  onDelete: () => void;
  
  // Styling
  darkModeClass?: string;
  label?: string;
  htmlFor?: string;
  required?: boolean;
  // Control upload button visibility
  hideUploadButton?: boolean;
  // Control download button visibility
  hideDownloadButton?: boolean;
  // Disable file upload and delete actions
  disabled?: boolean;
  // Hide delete button - when true, hides delete. Download is text button unless useIconDownloadWhenDeleteHidden.
  hideDeleteButton?: boolean;
  /** When hideDeleteButton is true, use icon download (same as two-action row) instead of labeled DOWNLOAD. Default false preserves other screens. */
  useIconDownloadWhenDeleteHidden?: boolean;
  uploadComplete?: boolean;
  className?: string;
  /** Form validation: library aligns label + file control border (#C81E24). */
  error?: boolean;
  /** Upload pipeline failure: library ProgressBar / simulate-upload (not field validation). */
  uploadError?: boolean;
  /**
   * When true, show confirmation modal before calling onDelete.
   * Default false keeps existing immediate-delete behavior for other screens.
   */
  confirmBeforeDelete?: boolean;
}

/**
 * Reusable FileUploadDisplay component that handles both file upload and file display states
 * Uses direct imports from library to avoid circular dependency with useLibraryComponents hook
 */
const FileUploadDisplay: React.FC<FileUploadDisplayProps> = ({
  hasUploadedFile,
  fileName,
  isUploading,
  onFileChange,
  placeholder = "Choose File",
  fileInputValue,
  fileInputKey,
  allowedTypes,
  toastMessage,
  onDownload,
  onDelete,
  darkModeClass,
  label,
  htmlFor,
  required = false,
  hideUploadButton = false,
  hideDownloadButton = false,
  disabled = false,
  hideDeleteButton = false,
  useIconDownloadWhenDeleteHidden = false,
  uploadComplete,
  className,
  error = false,
  uploadError = false,
  confirmBeforeDelete = false,
}) => {
  const { languageText } = useLanguageContext();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleRequestDelete = useCallback(() => {
    if (!confirmBeforeDelete) {
      onDelete();
      return;
    }
    setConfirmationTittle(
      languageText?.COMPONENT_SPECIFIC_CONTENT?.COMPLIANCE_DOCUMENTS?.DELETE_LEGACY_DOCUMENT_CONFIRM_TITLE
        || "Delete Document"
    );
    setConfirmationMessage(
      languageText?.MESSAGES?.CONFIRMATION_BOX_MESSAGES?.CONFIRM_MESSAGE?.DELETE_FILE_CONFIRM_MSG
        || "Are you sure you want to delete?"
    );
    setHighlightedConfirmationMessage("");
    setDeleteConfirmOpen(true);
  }, [confirmBeforeDelete, onDelete, languageText]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    onDelete();
  }, [onDelete]);

  return (
    <Box sx={fileUploadRootSx}>
      {/* Label */}
      {label && (
        <Label
          text={label}
          htmlFor={htmlFor}
          required={required}
          className={`${darkModeClass} ${className || ""}`}
          error={error}
        />
      )}
      
      {/* File Input or File Display */}
      {!hasUploadedFile ? (
        <Box
          sx={{
            '& .MuiInputBase-root.Mui-disabled': {
              backgroundColor: '#f5f5f5 !important',
            },
            '& .Mui-disabled .MuiInputBase-root': {
              backgroundColor: '#f5f5f5 !important',
            },
            '& .MuiOutlinedInput-root.Mui-disabled': {
              backgroundColor: '#f5f5f5 !important',
            },
            '& input[disabled]': {
              backgroundColor: '#f5f5f5 !important',
            },
            '& *[disabled]': {
              backgroundColor: '#f5f5f5 !important',
            },
          }}
        >
          <FileInput
            key={fileInputKey}
            onFileChange={onFileChange}
            placeholder={placeholder}
            value={fileInputValue}
            disabled={isUploading || disabled}
            allowedTypes={allowedTypes}
            toastMessage={toastMessage}
            error={error}
            uploadError={uploadError}
            {...(htmlFor ? { inputId: htmlFor } : {})}
            {...(uploadComplete !== undefined && { uploadComplete })}
          />
        </Box>
      ) : (
        <Box sx={{
          ...fileDisplayContainerSx,
          ...(disabled && { backgroundColor: '#f5f5f5' }),
          ...(error && {
            borderColor: '#C81E24',
            borderWidth: 1,
            borderStyle: 'solid',
            boxShadow: '0 0 0 1px #C81E24',
          }),
        }}>
          <FileNameTruncatedText
            title={fileName || ''}
            variant="body2"
            sx={fileNameTruncationSx}
          >
            {fileName}
          </FileNameTruncatedText>
          
          {/* Action Buttons Container */}
          <Box sx={actionButtonsContainerSx}>
            {/* Download Button - Always enabled, even when page is locked */}
            {!hideDownloadButton &&
              (hideDeleteButton && !useIconDownloadWhenDeleteHidden ? (
                <DownloadButton
                  text="DOWNLOAD"
                  onClick={onDownload}
                  size="medium"
                  primary
                />
              ) : (
                <MUIButton
                  size="small"
                  onClick={onDownload}
                  sx={fileActionButtonStyles(false)}
                  className="download-button-transparent"
                  title="Download file"
                >
                  <Image src={downloadButton} alt="Download" height={14} />
                </MUIButton>
              ))}
            
            {/* Delete Button */}
            {!hideDeleteButton && (
              <MUIButton
                size="small"
                onClick={handleRequestDelete}
                sx={fileActionButtonBaseSx}
                color="error"
                title="Delete file"
                disabled={disabled}
              >
                <Image src={deleteIcon} alt="Delete" height={14} />
              </MUIButton>
            )}
          </Box>
        </Box>
      )}

      {confirmBeforeDelete ? (
        <ConfirmationModal
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
          onDecline={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          confirmText={languageText?.BUTTONS?.DELETE ?? "Delete"}
          cancelText={languageText?.BUTTONS?.CANCEL_BUTTON ?? "Cancel"}
          showCancelButton
        />
      ) : null}
      
      {/* Upload Status */}
      {/* {isUploading && (
        <Typography variant="caption" color="primary" sx={uploadingTextSx}>
          Uploading...
        </Typography> this text not required
      )} */}
    </Box>
  );
};

export default FileUploadDisplay;
